import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RefreshCw, Search, Download } from "lucide-react";
import { http, errMsg, apiBaseUrl } from "@/lib/api";
import { SACard, timeAgo } from "@/components/superadmin/SAPrimitives";

export default function SuperAdminAudit() {
    const [params, setParams] = useSearchParams();
    const action = params.get("action") || "";
    const entity = params.get("entity_type") || "";
    const tenantId = params.get("tenant_id") || "";

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/superadmin/audit", {
                params: {
                    action: action || undefined,
                    entity_type: entity || undefined,
                    tenant_id: tenantId || undefined,
                    limit: 500,
                },
            });
            setEvents(data.events || []);
            setErr("");
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [action, entity, tenantId]);

    useEffect(() => { load(); }, [load]);

    const setParam = (k, v) => {
        const next = new URLSearchParams(params);
        if (!v) next.delete(k); else next.set(k, v);
        setParams(next, { replace: true });
    };

    return (
        <div className="space-y-5" data-testid="sa-audit-page">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[24px] font-semibold tracking-tight">Audit Log</h1>
                    <div className="mt-1 text-[12px] text-white/55">
                        Permanent record · {events.length} events
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <a
                        data-testid="sa-audit-export-csv"
                        href={(() => {
                            const p = new URLSearchParams();
                            if (action) p.set("action", action);
                            if (tenantId) p.set("tenant_id", tenantId);
                            const qs = p.toString();
                            return `${apiBaseUrl()}/api/superadmin/audit.csv${qs ? `?${qs}` : ""}`;
                        })()}
                        download
                        className="flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.02] px-3 py-1.5 text-[12px] text-white/80 transition-colors hover:border-[#00C2FF]/40 hover:text-[#00C2FF]"
                    >
                        <Download size={12} /> Export CSV
                    </a>
                    <button
                        onClick={load}
                        data-testid="sa-audit-refresh"
                        className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-[12px] text-white/75 hover:bg-white/5"
                    >
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            <SACard className="flex flex-wrap items-center gap-2 p-3">
                <div className="relative min-w-[220px] flex-1">
                    <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
                    <input
                        data-testid="sa-audit-action-filter"
                        defaultValue={action}
                        placeholder="Filter by action (e.g. tenant.suspended)…"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") setParam("action", e.currentTarget.value.trim());
                        }}
                        className="h-8 w-full rounded-md border border-white/5 bg-white/[0.03] pl-8 pr-3 text-[12.5px] text-white placeholder:text-white/30 focus:border-[#00C2FF]/50 focus:outline-none"
                    />
                </div>
                <input
                    data-testid="sa-audit-entity-filter"
                    defaultValue={entity}
                    placeholder="entity_type"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") setParam("entity_type", e.currentTarget.value.trim());
                    }}
                    className="h-8 w-40 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[12px] text-white placeholder:text-white/30 focus:outline-none"
                />
                <input
                    data-testid="sa-audit-tenant-filter"
                    defaultValue={tenantId}
                    placeholder="tenant_id"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") setParam("tenant_id", e.currentTarget.value.trim());
                    }}
                    className="h-8 w-56 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[12px] text-white placeholder:text-white/30 focus:outline-none"
                />
            </SACard>

            {err && <SACard className="text-red-300">{err}</SACard>}

            <SACard className="p-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-b border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                                <th className="px-5 py-2.5 font-medium">Action</th>
                                <th className="px-2 py-2.5 font-medium">Actor</th>
                                <th className="px-2 py-2.5 font-medium">Tenant</th>
                                <th className="px-2 py-2.5 font-medium">Entity</th>
                                <th className="px-2 py-2.5 font-medium">Detail</th>
                                <th className="px-5 py-2.5 font-medium">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((e, i) => (
                                <tr key={e.id || `${e.action}-${e.created_at || i}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                                    <td className="px-5 py-2.5 font-mono text-[11px] text-white/85">{e.action}</td>
                                    <td className="px-2 py-2.5 text-white/80">{e.actor_email || e.actor_id || "system"}</td>
                                    <td className="px-2 py-2.5">
                                        {e.tenant_id ? (
                                            <Link
                                                to={`/superadmin/tenants/${e.tenant_id}`}
                                                className="text-[#00C2FF] hover:underline"
                                            >
                                                {e.tenant_name || e.tenant_slug || e.tenant_id.slice(0, 8)}
                                            </Link>
                                        ) : (
                                            "—"
                                        )}
                                    </td>
                                    <td className="px-2 py-2.5 text-white/70">
                                        {e.entity_type || "—"}
                                        {e.entity_id && (
                                            <span className="ml-1 text-[10px] text-white/40">
                                                {e.entity_id.slice(0, 8)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-2 py-2.5 text-white/55">
                                        {e.detail ? JSON.stringify(e.detail).slice(0, 80) : "—"}
                                    </td>
                                    <td className="px-5 py-2.5 text-white/55">{timeAgo(e.created_at)}</td>
                                </tr>
                            ))}
                            {!loading && events.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-12 text-center text-white/40">
                                        No audit events match.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SACard>
        </div>
    );
}
